// Sequência OTIMISTA do botão de favorito — uma só, compartilhada por 6 das 7
// telas que favoritam: ficha de filme (MovieDetailsScreen), ficha de série
// (SeriesDetailsScreen), player de canal (PlayerScreen), player de série
// (SeriesPlayerScreen), player de VOD (VodPlayerScreen) e a ficha do mobile
// (MobileDetail).
//
// A 7ª é a tecla vermelha do controle na LiveScreen (`toggleFocusedFavorite`),
// que chama `addFavorite`/`removeFavorite` DIRETO e não passa por aqui: ali o
// estado do botão não é estado local, deriva de uma query (`favoriteChannelIds`,
// do `useLiveChannelList`), e pintar otimista pede mexer no cache do React
// Query. Ela não fica muda por isso: as duas funções não rejeitam mais, então
// o `invalidateQueries` que vem depois do await sempre roda.
//
// Pra re-derivar a lista (fora dos testes, do local-db e deste arquivo):
//   git grep -n -E "toggleFavoriteOptimistic\(|\b(add|remove)Favorite\(" -- apps/tv/src
//
// ── O defeito que isto fecha ──
// As seis telas faziam `await addFavorite(...)` e só ENTÃO `setFavorited(true)`.
// Duas consequências, e a segunda é a que o dono reportou:
//  1. LATÊNCIA VISÍVEL: num IDB lento (TV 2017 sob pressão de quota) a estrela
//     só acende depois da ida ao disco, muito além do orçamento de 100ms de
//     feedback de OK (docs/specs/ux-tv.md). O usuário aperta de novo achando
//     que não pegou.
//  2. FALHA SILENCIOSA: se a gravação REJEITAVA, o `setFavorited` nunca rodava
//     e a rejeição subia não tratada. A estrela simplesmente não virava —
//     "o botão favorito parou de funcionar".
//
// ── Por que otimista, e por que ainda assim reverte ──
// Pinta ANTES do await: o feedback é imediato e o caminho feliz (a esmagadora
// maioria) fica correto na hora. Mas `addFavorite`/`removeFavorite` devolvem se
// a operação PERSISTIU em alguma fonte (IDB ou o fallback de localStorage); se
// as duas recusarem, desfaz a pintura. Otimismo sem reversão seria pior que o
// defeito original: a tela passaria a MENTIR em vez de só não responder.

import type { Favorite } from '../local-db'
import { addFavorite, removeFavorite } from '../local-db'

type FavoriteInput = Omit<Favorite, 'providerScope'>

export interface ToggleFavoriteArgs {
  /** Estado ATUAL (o que a estrela mostra agora). */
  favorited: boolean
  /** id scopado, de `favoriteId(type, itemId)`. */
  favId: string
  /** Monta o registro a gravar. Função (e não valor) pra o caller só pagar a
   *  montagem no ramo que de fato adiciona. */
  makeInput: () => FavoriteInput
  /** Pinta a estrela. Chamado ANTES do await, e de novo (invertido) se falhar. */
  paint: (next: boolean) => void
}

/**
 * Alterna o favorito pintando otimista e revertendo se nada persistiu.
 *
 * @returns `true` se persistiu. `false` = a pintura já foi revertida.
 */
export async function toggleFavoriteOptimistic(args: ToggleFavoriteArgs): Promise<boolean> {
  const next = !args.favorited
  // 1. Feedback IMEDIATO — antes de qualquer await.
  args.paint(next)
  // 2. Persistência. Nenhuma das duas lança (o contrato do local-db), então não
  //    há try/catch aqui: o sinal de falha é o retorno, não a exceção.
  const persisted = next ? await addFavorite(args.makeInput()) : await removeFavorite(args.favId)
  // 3. Nem IDB nem fallback aceitaram → desfaz, pra a tela não mentir.
  if (!persisted) args.paint(!next)
  return persisted
}
